import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, Droplets, Pill, Utensils, PlayCircle, FileText, BookOpen, Link as LinkIcon, ExternalLink } from "lucide-react";

// ─── Defaults (fallback for legacy protocols without checklist_items/symptom_items) ───
const _DEFAULT_CHECKLIST = [
  { key: "water", label: "Hidratação (2L+)", icon: "water" },
  { key: "evacuation", label: "Evacuação", icon: "circle" },
  { key: "supplementation", label: "Suplementação", icon: "pill" },
  { key: "food", label: "Alimentação conforme plano", icon: "utensils" },
];
const _DEFAULT_SYMPTOMS = [
  { key: "gases", label: "Gases", min: 0, max: 10 },
  { key: "dor", label: "Dor abdominal", min: 0, max: 10 },
  { key: "energia", label: "Energia", min: 0, max: 10 },
  { key: "distensao", label: "Distensão", min: 0, max: 10 },
  { key: "evacuacao", label: "Qualidade evacuação", min: 0, max: 10 },
];
const _ICON_MAP = {
  water: Droplets,
  pill: Pill,
  utensils: Utensils,
  circle: Circle,
  check: CheckCircle2,
};

export default function ProtocolsPage() {
  const [protocols, setProtocols] = useState([]);
  const [active, setActive] = useState(null);
  const [checklist, setChecklist] = useState({ symptom_scores: {} });
  const [symptoms, setSymptoms] = useState({});
  const [pendingSwitchId, setPendingSwitchId] = useState(null);
  const [pendingSwitchMsg, setPendingSwitchMsg] = useState("");

  const loadAll = async () => {
    try {
      const [all, mine, today] = await Promise.all([
        api.get("/protocols"),
        api.get("/protocols/me/active"),
        api.get("/checklist/daily"),
      ]);
      setProtocols(all.data);
      setActive(mine.data);
      setChecklist(today.data || {});
      setSymptoms((s) => ({ ...s, ...(today.data?.symptom_scores || {}) }));
    } catch (_) { /* noop */ }
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 20000);
    return () => clearInterval(t);
  }, []);

  const doStart = async (protocol_id, force = false) => {
    try {
      await api.post("/protocols/start", { protocol_id, force });
      setPendingSwitchId(null);
      await loadAll();
      toast.success("Protocolo iniciado.");
    } catch (err) {
      if (err.response?.status === 409) {
        setPendingSwitchId(protocol_id);
        setPendingSwitchMsg(formatApiError(err.response?.data?.detail));
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Não foi possível iniciar o protocolo.");
      }
    }
  };

  const saveChecklist = async (patch = {}) => {
    const next = { ...checklist, ...patch, symptom_scores: symptoms };
    setChecklist(next);
    try { await api.post("/checklist/daily", next); } catch (_) { toast.error("Erro ao salvar checklist."); }
  };

  const saveSymptoms = async () => {
    try {
      await api.post("/checklist/daily", { ...checklist, symptom_scores: symptoms });
      toast.success("Score de sintomas salvo.");
    } catch (_) { toast.error("Erro ao salvar."); }
  };

  const protocol = active?.protocol;
  const phase = active?.current_phase;

  // Dynamic checklist / symptom / education from the protocol snapshot,
  // falling back to app-wide defaults for legacy protocols.
  const checklistItems = useMemo(
    () => (protocol?.checklist_items?.length ? protocol.checklist_items : _DEFAULT_CHECKLIST),
    [protocol]
  );
  const symptomItems = useMemo(
    () => (protocol?.symptom_items?.length ? protocol.symptom_items : _DEFAULT_SYMPTOMS),
    [protocol]
  );
  const content = protocol?.education?.items || [];

  return (
    <div className="space-y-8" data-testid="protocols-page">
      <div>
        <div className="overline text-accent mb-3">Protocolos</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Suas linhas de cuidado</h1>
      </div>

      {protocol && phase && (
        <div className="bg-card border border-border rounded-sm p-8" data-testid="active-protocol-section">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="overline text-success mb-2">Protocolo ativo</div>
              <h2 className="font-heading text-2xl font-bold">{protocol.name}</h2>
              <p className="text-sm text-muted-foreground mt-1">{phase.name} · Dia {active.current_day}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {protocol.phases.map((ph) => {
              const isActive = ph.id === phase.id;
              const isPast = ph.day_end < active.current_day;
              return (
                <div
                  key={ph.id}
                  className={`border rounded-sm p-4 ${isActive ? "border-primary bg-primary/5" : isPast ? "border-success/30 bg-success/5" : "border-border"}`}
                  data-testid={`phase-${ph.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="overline text-muted-foreground">Dias {ph.day_start}–{ph.day_end}</div>
                    {isPast && <CheckCircle2 className="w-4 h-4 text-success" />}
                    {isActive && <span className="text-xs font-semibold text-primary">Ativa</span>}
                  </div>
                  <h4 className="font-heading font-semibold text-sm">{ph.name}</h4>
                  <p className="text-xs text-muted-foreground mt-2">{ph.expected_symptoms}</p>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <div className="overline text-muted-foreground mb-3">Checklist diário</div>
              <div className="space-y-2">
                {checklistItems.map(({ key, label, icon }) => {
                  const Icon = _ICON_MAP[icon] || Circle;
                  const done = !!checklist?.[key];
                  return (
                    <button
                      key={key}
                      onClick={() => saveChecklist({ [key]: !done })}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm border transition-colors ${done ? "border-success/40 bg-success/5" : "border-border bg-card hover:bg-muted"}`}
                      data-testid={`checklist-${key}`}
                    >
                      <div className={`w-6 h-6 rounded-sm grid place-items-center ${done ? "bg-success text-white" : "bg-muted"}`}>
                        {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-3.5 h-3.5 text-muted-foreground" />}
                      </div>
                      <span className="text-sm font-medium text-left flex-1">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="overline text-muted-foreground mb-3">Score de sintomas (0–10)</div>
              <div className="space-y-4">
                {symptomItems.map(({ key, label, min = 0, max = 10 }) => {
                  const val = symptoms[key] ?? Math.round((min + max) / 2);
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-sm mb-1.5">
                        <span>{label}</span>
                        <span className="font-semibold text-primary" data-testid={`symptom-${key}-value`}>{val}</span>
                      </div>
                      <input
                        type="range" min={min} max={max} value={val}
                        onChange={(e) => setSymptoms({ ...symptoms, [key]: Number(e.target.value) })}
                        className="w-full accent-primary"
                        data-testid={`symptom-${key}-input`}
                      />
                    </div>
                  );
                })}
                <button
                  onClick={saveSymptoms}
                  className="w-full rounded-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
                  data-testid="save-symptoms-btn"
                >
                  Salvar score
                </button>
              </div>
            </div>
          </div>

          {/* Educational content */}
          {content.length > 0 && (
            <div className="mt-10">
              <div className="overline text-muted-foreground mb-3">Conteúdo educativo</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {content.map((c, i) => {
                  const Icon = c.type === "video" ? PlayCircle : c.type === "pdf" ? FileText : BookOpen;
                  return (
                    <a key={i} href={c.url} target="_blank" rel="noreferrer" className="bg-card border border-border rounded-sm p-4 hover:border-primary transition-colors group" data-testid={`content-${i}`}>
                      <div className="flex items-center justify-between mb-3">
                        <Icon className="w-5 h-5 text-primary group-hover:scale-110 transition-transform" />
                        <span className="overline text-muted-foreground">{c.type}</span>
                      </div>
                      <div className="font-heading font-semibold text-sm">{c.title}</div>
                      {c.duration && <div className="text-xs text-muted-foreground mt-1">⏱ {c.duration}</div>}
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Educational content configured for this patient's protocol */}
      {protocol?.education && (protocol.education.html || (protocol.education.links || []).length > 0 || (protocol.education.pdfs || []).length > 0) && (
        <div className="bg-card border border-border rounded-sm p-6" data-testid="protocol-education-section">
          <div className="overline text-gold mb-3 inline-flex items-center gap-2"><BookOpen className="w-3.5 h-3.5" /> Conteúdo educativo do protocolo</div>
          {protocol.education.html && (
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm text-foreground mb-4" data-testid="education-html">
              {protocol.education.html}
            </div>
          )}
          {(protocol.education.links || []).length > 0 && (
            <div className="mb-4">
              <div className="overline text-muted-foreground mb-2 inline-flex items-center gap-1"><LinkIcon className="w-3 h-3" /> Links</div>
              <ul className="space-y-1">
                {protocol.education.links.map((l, i) => (
                  <li key={i}>
                    <a href={l.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid={`edu-link-${i}`}>
                      <ExternalLink className="w-3 h-3" /> {l.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(protocol.education.pdfs || []).length > 0 && (
            <div>
              <div className="overline text-muted-foreground mb-2 inline-flex items-center gap-1"><FileText className="w-3 h-3" /> Materiais em PDF</div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {protocol.education.pdfs.map((pdf, i) => (
                  <li key={i}>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${pdf.url}`} target="_blank" rel="noreferrer"
                      className="flex items-center gap-3 bg-muted/40 border border-border rounded-sm px-3 py-2 hover:border-primary transition-colors"
                      data-testid={`edu-pdf-${i}`}>
                      <FileText className="w-4 h-4 text-primary flex-none" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{pdf.title}</div>
                        {pdf.filename && <div className="text-xs text-muted-foreground truncate">{pdf.filename}</div>}
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Available protocols */}
      <div>
        <div className="overline text-muted-foreground mb-4">Todos os protocolos disponíveis</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {protocols.map((p) => {
            const isActive = active?.active_protocol?.protocol_id === p.id;
            return (
              <div key={p.id} className="bg-card border border-border rounded-sm p-6 flex flex-col" data-testid={`protocol-item-${p.id}`}>
                <div className="overline text-muted-foreground mb-2">{p.category}</div>
                <h3 className="font-heading font-semibold text-lg mb-2">{p.name}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">{p.description}</p>
                <div className="mb-4 text-xs text-muted-foreground">Duração: {p.duration_days} dias · {p.phases.length} fases</div>
                <div className="flex flex-wrap gap-2 mb-5">
                  {p.indications.slice(0, 3).map((i) => (
                    <span key={i} className="text-xs bg-muted px-2 py-1 rounded-sm">{i}</span>
                  ))}
                </div>
                <button
                  onClick={() => doStart(p.id)}
                  disabled={isActive}
                  className="mt-auto rounded-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
                  data-testid={`start-protocol-${p.id}`}
                >
                  {isActive ? "Ativo" : "Iniciar protocolo"}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Confirmation modal */}
      {pendingSwitchId && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={() => setPendingSwitchId(null)} data-testid="switch-confirm-modal">
          <div className="bg-card border border-border rounded-sm p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="overline text-destructive mb-2">Confirmar troca de protocolo</div>
            <h3 className="font-heading text-lg font-semibold mb-2">Tem certeza?</h3>
            <p className="text-sm text-muted-foreground mb-5">{pendingSwitchMsg}</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setPendingSwitchId(null)} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors" data-testid="switch-cancel">Cancelar</button>
              <button onClick={() => doStart(pendingSwitchId, true)} className="px-4 py-2 rounded-sm bg-destructive text-destructive-foreground text-sm font-semibold hover:bg-destructive/90 transition-colors" data-testid="switch-confirm">Trocar de protocolo</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
